import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from './AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { AUTH_FIELD_CLASS, AUTH_PRIMARY_BUTTON } from '@/components/auth/AuthShell';

/// The second-factor challenge shown after a password login when the account
/// has MFA enrolled. Previously this lived inline inside `LoginPage`'s render;
/// extracting it to its own component keeps the login surface readable and
/// gives the challenge a single, testable home.
///
/// NOTE: this is a self-contained COMPONENT, not a router route — the MFA token
/// captured at login lives in the in-memory `AuthProvider` context, and a real
/// route navigation (or refresh) would drop it. `LoginPage` renders this when
/// `requiresMfa` is true; the AuthProvider context is shared, so `verifyMfa`
/// here completes the same in-flight challenge.
export default function MfaChallengePage() {
  const { verifyMfa, logout } = useAuth();
  // This sign-in step is used up or expired: no code will work, so back to the start.
  const [spent, setSpent] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  // The recovery codes shown at setup sign in when the phone is lost.
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyMfa(mfaCode);
    } catch (err: any) {
      const code = err.response?.data?.code;
      setSpent(code === 'mfa_token_invalid' || code === 'mfa_attempts_exhausted');
      setError(err.response?.data?.error || 'Invalid MFA code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleMfa}>
      {error && (
        <Alert variant="error" className="mb-3 rounded-xl">
          <AlertDescription>
            {error}
            {spent && (
              <>
                {' '}
                <button type="button" className="font-medium underline" onClick={logout}>Sign in again</button>
              </>
            )}
          </AlertDescription>
        </Alert>
      )}
      <p className="mb-3 text-sm text-muted-foreground">
        {useRecoveryCode
          ? 'Enter one of the recovery codes you saved when you set up two-step sign-in. Each works once.'
          : 'Enter the 6-digit code from your authenticator app.'}
      </p>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="mfa-code" className="text-muted-foreground">
          {useRecoveryCode ? 'Recovery code' : 'MFA Code'}
        </Label>
        {useRecoveryCode ? (
          <Input
            id="mfa-code"
            required
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value.trim().slice(0, 32))}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            autoFocus
            className={cn(AUTH_FIELD_CLASS, 'text-center tracking-[0.1em]')}
          />
        ) : (
          <Input
            id="mfa-code"
            required
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            maxLength={6}
            pattern="[0-9]{6}"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            className={cn(AUTH_FIELD_CLASS, 'text-center tracking-[0.3em]')}
          />
        )}
      </div>
      <Button
        type="submit"
        disabled={loading || (useRecoveryCode ? mfaCode.length < 6 : mfaCode.length !== 6)}
        className={cn('mt-4', AUTH_PRIMARY_BUTTON)}
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Verify'}
      </Button>
      <button
        type="button"
        onClick={() => { setUseRecoveryCode(v => !v); setMfaCode(''); setError(''); }}
        className="mt-3 w-full text-center text-sm text-muted-foreground hover:text-primary"
      >
        {useRecoveryCode ? 'Use my authenticator app instead' : 'Lost your phone? Use a recovery code'}
      </button>
    </form>
  );
}
